/**
 * useClosingDisclosure - Custom hook for accessing Closing Disclosure context
 */

import * as React from 'react';
import type {
  ClosingDisclosureContextValue,
  ClosingDisclosureData,
} from '../types/closingDisclosureData';
import { ClosingDisclosureContext } from './ClosingDisclosureContext';

/**
 * Hook to access the full Closing Disclosure context
 * @throws Error if used outside of ClosingDisclosureProvider
 */
export function useClosingDisclosure(): ClosingDisclosureContextValue {
  const context = React.useContext(ClosingDisclosureContext);
  if (!context) {
    throw new Error('useClosingDisclosure must be used within a ClosingDisclosureProvider');
  }
  return context;
}

/**
 * Hook to access and update a specific page's data
 */
export function useClosingDisclosurePage<K extends 'page1' | 'page2' | 'page3' | 'page4' | 'page5'>(
  page: K,
): [ClosingDisclosureData[K], (data: ClosingDisclosureData[K]) => void] {
  const { formData, updatePage } = useClosingDisclosure();

  const pageData = formData[page];
  const setPageData = React.useCallback(
    (data: ClosingDisclosureData[K]) => updatePage(page, data),
    [page, updatePage],
  );

  return [pageData, setPageData];
}

/**
 * Hook to access and update a specific field by path
 * @example const [value, setValue] = useClosingDisclosureField('page1.header.closingInformation.closingDate');
 */
export function useClosingDisclosureField<T = unknown>(
  path: string,
): [T | undefined, (value: T) => void] {
  const { formData, updateField } = useClosingDisclosure();

  // Get value by path
  const getValue = React.useCallback(() => {
    const keys = path.split('.');
    let current: unknown = formData;
    for (const key of keys) {
      if (current == null || typeof current !== 'object') {
        return undefined;
      }
      current = (current as Record<string, unknown>)[key];
    }
    return current as T | undefined;
  }, [formData, path]);

  const value = getValue();
  const setValue = React.useCallback(
    (newValue: T) => updateField(path, newValue),
    [path, updateField],
  );

  return [value, setValue];
}

/**
 * Hook to get form metadata
 */
export function useClosingDisclosureMetadata() {
  const { formData, editable, isLoading, error } = useClosingDisclosure();

  return {
    loanId: formData.loanId,
    formId: formData.formId,
    totalPages: formData.totalPages,
    editable,
    isLoading,
    error,
  };
}

/**
 * Hook to get all page data at once (for preview/print)
 */
export function useClosingDisclosureAllPages() {
  const { formData } = useClosingDisclosure();

  return {
    page1: formData.page1,
    page2: formData.page2,
    page3: formData.page3,
    page4: formData.page4,
    page5: formData.page5,
  };
}
